import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

function Host() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [ws, setWs] = useState(null);
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const loadSession = useCallback(async () => {
    try {
      const data = await api.getSession(sessionId);
      setSession(data.session);
      setParticipants(data.participants || []);
      setStatus(data.session.status);
    } catch (err) {
      setError(err.message);
    }
  }, [sessionId]);

  const connectWebSocket = useCallback(() => {
    const socket = api.connectWebSocket(sessionId, null, 'host');

    socket.onopen = () => {
      console.log('Host WebSocket connected');
    };

    socket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleMessage(message);
    };

    socket.onclose = () => {
      console.log('WebSocket disconnected');
    };

    setWs(socket);
    return () => socket.close();
  }, [sessionId]);

  useEffect(() => {
    loadSession();
    const cleanup = connectWebSocket();
    return cleanup;
  }, [loadSession, connectWebSocket]);

  const handleMessage = (message) => {
    const { type, payload } = message;

    switch (type) {
      case 'player_joined':
        setParticipants(prev => [...prev, payload.participant]);
        break;

      case 'player_left':
        setParticipants(prev => prev.filter(p => p.id !== payload.participantId));
        break;

      case 'answer_received':
        setAnsweredCount(payload.totalAnswered);
        break;

      default:
        console.log('Host message:', type, payload);
    }
  };

  const startQuiz = async () => {
    try {
      await api.startSession(sessionId);
      setStatus('active');
    } catch (err) {
      setError(err.message);
    }
  };

  const showNextQuestion = async () => {
    try {
      const nextIndex = questionIndex + 1;
      const data = await api.nextQuestion(sessionId, nextIndex);
      setCurrentQuestion(data.question);
      setQuestionIndex(nextIndex);
      setAnsweredCount(0);
    } catch (err) {
      setError(err.message);
    }
  };

  const revealAnswer = () => {
    if (ws) {
      ws.send(JSON.stringify({ type: 'reveal_answer', payload: {} }));
    }
  };

  const showLeaderboard = () => {
    if (ws) {
      ws.send(JSON.stringify({ type: 'show_leaderboard', payload: { topN: 10 } }));
    }
  };

  const endQuiz = async () => {
    try {
      await api.endSession(sessionId);
      setStatus('finished');
    } catch (err) {
      setError(err.message);
    }
  };

  if (error) {
    return (
      <div className="host-page">
        <div className="error-message">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Back to Home
        </button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="host-page">
        <div className="loading">Loading session...</div>
      </div>
    );
  }

  return (
    <div className="host-page">
      <div className="host-header">
        <div>
          <h1>{session.title || 'Quiz Session'}</h1>
          <div className="session-code">{session.joinCode}</div>
        </div>
        
        <div className="host-controls">
          {status === 'lobby' && (
            <button 
              className="btn btn-primary" 
              onClick={startQuiz}
              disabled={participants.length === 0}
            >
              Start Quiz ({participants.length} players)
            </button>
          )}
          
          {status === 'active' && (
            <>
              {!currentQuestion ? (
                <button className="btn btn-primary" onClick={showNextQuestion}>
                  Show First Question
                </button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={revealAnswer}>
                    Reveal Answer
                  </button>
                  <button className="btn btn-secondary" onClick={showLeaderboard}>
                    Show Leaderboard
                  </button>
                  {questionIndex < (session.questionCount || 10) ? (
                    <button className="btn btn-primary" onClick={showNextQuestion}>
                      Next Question
                    </button>
                  ) : (
                    <button className="btn btn-primary" onClick={endQuiz}>
                      End Quiz
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="participants-list">
        <h3>👥 Players ({participants.length})</h3>
        {participants.length === 0 ? (
          <p style={{ color: '#a0a0a0' }}>
            Share the code <strong>{session.joinCode}</strong> for players to join!
          </p>
        ) : (
          participants.map((p) => (
            <div key={p.id} className="participant">
              <span className="participant-avatar">{p.avatarEmoji || '😀'}</span>
              <span className="participant-name">{p.playerName}</span>
              <span className="participant-score">{p.score || 0} pts</span>
            </div>
          ))
        )}
      </div>

      {currentQuestion && (
        <div className="question-preview">
          <div className="question-number" style={{ marginBottom: 10 }}>
            Question {currentQuestion.index} of {currentQuestion.total}
          </div>
          <h2>{currentQuestion.questionText}</h2>
          
          <div style={{ marginTop: 20 }}>
            {currentQuestion.answers.map((a) => (
              <div key={a.label} style={{ 
                padding: '10px 20px', 
                margin: '10px 0',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 8
              }}>
                <strong>{a.label}.</strong> {a.text}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20, color: '#a0a0a0' }}>
            {answeredCount} of {participants.length} answered
          </div>
        </div>
      )}

      {status === 'finished' && (
        <div className="final-results">
          <div className="trophy">🏆</div>
          <h1>Quiz Complete!</h1>
          <button 
            className="btn btn-primary" 
            style={{ maxWidth: 300, marginTop: 20 }}
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
}

export default Host;
